import React, { useEffect, useState } from 'react';
import { MapPin, Home, Calendar, Clock, BedDouble, Bath, Square as SquareFoot, Heart, Share2 } from 'lucide-react';
import { useProperty } from '../context/PropertyContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Button from '../components/common/Button';
import { formatCurrency, formatDate } from '../utils/formatters';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { apiService } from '../services/api';

const PropertyDetailsPage: React.FC = () => {
  const { getProperty, toggleFavorite, favorites } = useProperty();
  const { id: propertyId } = useParams<{ id: string }>();
  const { user, isAuthenticated, useTokens } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeImage, setActiveImage] = useState(0);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [fallbackProperty, setFallbackProperty] = useState<any | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<{contact_name: string; contact_email: string; contact_phone: string} | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);

    if (!propertyId) {
        // ne prekidaj render! samo prikaži placeholder
        return <div className="min-h-screen flex items-center justify-center text-gray-600">Invalid property ID</div>;
    }
  let property = getProperty(propertyId);

  useEffect(() => {
    const load = async () => {
      if (!property) {
        try {
          setDetailsError(null);
          setLoadingDetails(true);
          const l = await apiService.getPublicListing(propertyId);
          const mapped = {
            id: l.id,
            title: l.title,
            description: l.description ?? '',
            price: l.price ?? 0,
            address: {
              street: l.address ?? '',
              city: l.city ?? '',
              state: '',
              zipCode: l.zip_code ?? l.zipCode ?? '',
              country: l.country ?? ''
            },
            images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
            type: 'apartment',
            status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
            features: {
              bedrooms: l.rooms ?? 0,
              bathrooms: l.bathrooms ?? 0,
              area: Number(l.area_m2 ?? l.areaM2 ?? 0),
              garage: false,
              yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
            },
            amenities: [],
            contactInfo: { name: '', phone: '', email: '' },
            owner: l.owner_user_id ?? l.ownerUserId ?? '',
            createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
            updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
          };
          setFallbackProperty(mapped);
        } catch (e: any) {
          setDetailsError(e?.message || t('home.errors.failedToLoad'));
        } finally {
          setLoadingDetails(false);
        }
      }
    };
    load();
  }, [propertyId, property, t]);

  // Ensure active image index stays in range if images change
  useEffect(() => {
    if (property?.images?.length && activeImage >= property.images.length) {
      setActiveImage(0);
    }
  }, [property?.images?.length]);

  property = property || fallbackProperty || undefined;
  const isFavorite = favorites.includes(propertyId);
  const isOwner = isAuthenticated && property && property.owner === user?.id;

  // Load contact info - directly check backend if user owns or purchased
  useEffect(() => {
    const loadContact = async () => {
      // Reset state first
      setShowContactInfo(false);
      setContactInfo(null);
      
      if (!isAuthenticated || !property) {
        return;
      }
      
      // Always try to fetch contact info from backend
      // Backend will return it if user is owner OR has purchased it
      try {
        setLoadingContact(true);
        const info = await apiService.getContactInfo(propertyId);
        // If we get here, user has access (either owner or purchased)
        setContactInfo(info);
        setShowContactInfo(true);
      } catch (error: any) {
        // If 403 Forbidden, user doesn't have access yet
        if (error?.message?.includes('Forbidden') || error?.message?.includes('403')) {
          // User hasn't purchased yet - this is normal
          setShowContactInfo(false);
          setContactInfo(null);
        } else {
          console.error('Failed to load contact info:', error);
        }
      } finally {
        setLoadingContact(false);
      }
    };
    loadContact();
  }, [propertyId, isAuthenticated, property]);

  const handleContactReveal = () => {
    if (!isAuthenticated) {
      const redirectTo = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
      return;
    }
    
    // If user owns the property, show contact immediately
    if (isOwner) {
      setShowContactInfo(true);
      return;
    }
    
    if (user?.purchasedContacts?.includes(propertyId)) {
      setShowContactInfo(true);
    } else {
      setShowConfirmDialog(true);
    }
  };

  const confirmContactReveal = async () => {
    setShowConfirmDialog(false);
    
    try {
      // Use tokens - this will add to database and refresh user
      const success = await useTokens(propertyId);
      
      if (success) {
        // Directly fetch contact info from backend
        // Backend checks database directly so it should be available immediately
        try {
          const info = await apiService.getContactInfo(propertyId);
          setContactInfo(info);
          setShowContactInfo(true);
        } catch (fetchError) {
          console.error('Failed to fetch contact after purchase:', fetchError);
        }
      }
    } catch (error: any) {
      console.error('Failed to purchase contact:', error);
      
      // If contact is already purchased, fetch it anyway
      if (error?.message?.includes('already purchased')) {
        try {
          const info = await apiService.getContactInfo(propertyId);
          setContactInfo(info);
          setShowContactInfo(true);
        } catch (fetchError) {
          console.error('Failed to fetch already purchased contact:', fetchError);
        }
      }
    }
  };

  const contactRevealed = isAuthenticated && !!user?.purchasedContacts?.includes(propertyId);
  const hasEnoughTokens = isAuthenticated && (user?.tokens || 0) > 0;

  const formatLocation = (parts: Array<string | undefined>) =>
    parts.filter((part) => part && part.trim().length > 0).join(', ');
  const inferredZip = property?.address?.zipCode
    || property?.address?.street?.match(/\b\d{5}\b/)?.[0]
    || '';
  const cityLine = [property?.address?.city, inferredZip].filter(Boolean).join(' ');
  const locationDisplay = contactRevealed
    ? formatLocation([property?.address?.street, cityLine, property?.address?.state, property?.address?.country])
    : formatLocation([cityLine, property?.address?.state, property?.address?.country]);


    return (
        <div className="min-h-screen bg-gray-50 py-8">
            {/* --- LOADING --- */}
            {loadingDetails && !property && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse space-y-6">
                        <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-96 bg-gray-200 rounded"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-4">
                                <div className="h-40 bg-gray-200 rounded"></div>
                                <div className="h-64 bg-gray-200 rounded"></div>
                            </div>
                            <div className="h-72 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- NOT FOUND --- */}
            {!loadingDetails && !property && (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            {t('property.notFound')}
                        </h2>
                        <p className="text-gray-600 mb-8">
                            {detailsError || t('property.notFoundMessage')}
                        </p>
                        <Button onClick={() => navigate('/')}>
                            {t('common.backToHome')}
                        </Button>
                    </div>
                </div>
            )}

            {/* --- MAIN CONTENT --- */}
            {property && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Button */}
                    <div className="mb-8">
                        <Button
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="flex items-center"
                        >
                            <svg
                                className="w-5 h-5 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                />
                            </svg>
                            {t('common.backToListings')}
                        </Button>
                    </div>

                    {/* Header */}
                    <div className="bg-white rounded-2xl border border-neutral-200 shadow-soft p-6 mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                        property.status === 'sale'
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'bg-accent-50 text-accent-700'
                                    }`}>
                                        {property.status === 'sale' ? t('property.forSale') : t('property.forRent')}
                                    </span>
                                    <span className="text-sm text-neutral-500">
                                        {t('property.listedOn', { date: formatDate(property.createdAt) })}
                                    </span>
                                </div>
                                <h1 className="text-2xl lg:text-3xl font-semibold text-neutral-900">
                                    {property.title}
                                </h1>
                                <div className="flex items-center text-neutral-600">
                                    <MapPin size={18} className="mr-2" />
                                    <span>{locationDisplay}</span>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={() => toggleFavorite(property.id)}
                                    className={`px-4 py-2 rounded-full flex items-center border transition-all ${
                                        isFavorite
                                            ? 'bg-red-500 text-white border-red-500'
                                            : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                                    }`}
                                >
                                    <Heart size={18} className={isFavorite ? 'fill-current' : ''} />
                                    <span className="ml-2 text-sm font-medium">
                                        {isFavorite ? t('property.saved') : t('property.save')}
                                    </span>
                                </button>
                                <button className="px-4 py-2 rounded-full bg-white text-neutral-600 border border-neutral-200 flex items-center hover:border-neutral-300 transition-all">
                                    <Share2 size={18} />
                                    <span className="ml-2 text-sm font-medium">{t('property.share')}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 overflow-hidden mb-8">
                        <div className="relative aspect-video bg-neutral-100">
                            {property.images.length > 0 ? (
                                <img
                                    src={property.images[activeImage]}
                                    alt={property.title}
                                    className="object-cover w-full h-full"
                                />
                            ) : (
                                <div className="w-full h-full bg-neutral-100 flex items-center justify-center text-neutral-500">
                                    {t('property.noImages')}
                                </div>
                            )}
                        </div>
                        {property.images.length > 0 && (
                            <div className="p-4 flex gap-3 overflow-x-auto">
                                {property.images.map((image, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActiveImage(index)}
                                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border ${
                                            activeImage === index ? 'ring-2 ring-primary-500 border-primary-200' : 'border-neutral-200'
                                        }`}
                                    >
                                        <img
                                            src={image}
                                            alt={`${property.title} - Image ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Content grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                        {/* Left content (details) */}
                        <div className="lg:col-span-2">
                            <div className="bg-white p-6 rounded-2xl shadow-soft border border-neutral-200 mb-8">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <span className="text-2xl lg:text-3xl font-semibold text-neutral-900">
                                            {property.status === 'rent'
                                                ? `${formatCurrency(property.price)}/${t('property.perMonth')}`
                                                : formatCurrency(property.price)}
                                        </span>
                                    </div>
                                    <div className="mt-4 md:mt-0 flex items-center text-gray-600">
                                        <Home size={18} className="mr-2" />
                                        <span>{t(`property.type.${property.type}`)}</span>
                                        <span className="mx-2">•</span>
                                        <Calendar size={18} className="mr-2" />
                                        <span>
                    {t('property.builtIn', {
                        year: property.features.yearBuilt,
                    })}
                  </span>
                                    </div>
                                </div>
                            </div>

                            {/* Details */}
                            <div className="bg-white p-6 rounded-2xl shadow-soft border border-neutral-200 mb-8">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    {t('property.details')}
                                </h2>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
                                    <div className="flex items-center">
                                        <BedDouble size={22} className="text-brand-dark mr-3" />
                                        <div>
                                            <p className="text-gray-500 text-sm">
                                                {t('property.bedrooms')}
                                            </p>
                                            <p className="font-semibold">
                                                {property.features.bedrooms}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <Bath size={22} className="text-brand-dark mr-3" />
                                        <div>
                                            <p className="text-gray-500 text-sm">
                                                {t('property.bathrooms')}
                                            </p>
                                            <p className="font-semibold">
                                                {property.features.bathrooms}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <SquareFoot size={22} className="text-brand-dark mr-3" />
                                        <div>
                                            <p className="text-gray-500 text-sm">
                                                {t('property.area')}
                                            </p>
                                            <p className="font-semibold">
                                                {property.features.area.toLocaleString()} {t('property.sqft')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <h3 className="text-base font-semibold text-gray-900 mb-2">
                                    {t('property.description')}
                                </h3>
                                <p className="text-gray-700 mb-6">{property.description}</p>

                                <h3 className="text-base font-semibold text-gray-900 mb-2">
                                    {t('property.amenities')}
                                </h3>
                                <div className="grid grid-cols-2 gap-2 mb-4">
                                    {property.amenities.map((amenity, index) => (
                                        <div key={index} className="flex items-center">
                                            <div className="w-2 h-2 bg-brand-dark rounded-full mr-2"></div>
                                            <span className="text-gray-700">{amenity}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center text-gray-500 text-sm">
                                    <Clock size={14} className="mr-1" />
                                    <span>
                                        {t('property.listedOn', { date: formatDate(property.createdAt) })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Right sidebar (contact) */}
                        <div className="lg:col-span-1">
                            <div className="bg-white p-6 rounded-2xl shadow-soft border border-neutral-200 mb-8 sticky top-24">
                                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                                    {t('property.contactInfo')}
                                </h2>

                                {showContactInfo && contactInfo ? (
                                    <div className="space-y-4">
                                        <div className={`p-4 border rounded-lg ${isOwner ? 'border-primary-200 bg-primary-50' : 'border-secondary-200 bg-secondary-50'}`}>
                                            <h3 className={`text-base font-semibold mb-1 ${isOwner ? 'text-primary-700' : 'text-brand-dark'}`}>
                                                {isOwner ? t('property.yourProperty') : t('property.contactRevealed')}
                                            </h3>
                                            <p className={`text-sm mb-3 ${isOwner ? 'text-primary-600' : 'text-secondary-700'}`}>
                                                {isOwner ? t('property.yourPropertyMessage') : t('property.contactRevealedMessage')}
                                            </p>
                                            {loadingContact ? (
                                                <p className="text-gray-600">
                                                    {t('home.errors.loadingContactInfo')}
                                                </p>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div>
                                                        <p className="text-gray-500 text-sm">{t('property.name')}</p>
                                                        <p className="font-semibold">
                                                            {contactInfo.contact_name || 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500 text-sm">{t('property.phone')}</p>
                                                        <p className="font-semibold">
                                                            <a
                                                                href={`tel:${contactInfo.contact_phone}`}
                                                                className="text-brand-dark hover:underline"
                                                            >
                                                                {contactInfo.contact_phone || 'N/A'}
                                                            </a>
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500 text-sm">{t('property.email')}</p>
                                                        <p className="font-semibold">
                                                            <a
                                                                href={`mailto:${contactInfo.contact_email}`}
                                                                className="text-brand-dark hover:underline"
                                                            >
                                                                {contactInfo.contact_email || 'N/A'}
                                                            </a>
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : isOwner ? (
                                    <div className="text-center p-4 border border-primary-200 bg-primary-50 rounded-lg">
                                        <div className="mb-4">
                                            <h3 className="text-base font-semibold text-brand-dark mb-2">
                                                {t('property.yourProperty')}
                                            </h3>
                                            <p className="text-primary-700 text-sm">
                                                {t('property.yourPropertyMessage')}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={handleContactReveal}
                                            fullWidth
                                        >
                                            {t('property.contactInfo')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center p-4 border border-primary-200 bg-primary-50 rounded-lg">
                                        <div className="mb-4">
                                            <div className="text-3xl font-semibold text-brand-dark mb-1">₮ 1</div>
                                            <p className="text-primary-700">{t('property.contactInfoCost')}</p>
                                        </div>
                                        <Button
                                            onClick={handleContactReveal}
                                            disabled={!hasEnoughTokens}
                                            fullWidth
                                        >
                                            {hasEnoughTokens
                                                ? t('property.useToken')
                                                : t('property.notEnoughTokens')}
                                        </Button>
                                        {!hasEnoughTokens && (
                                            <Button
                                                variant="outline"
                                                onClick={() => navigate('/tokens')}
                                                className="mt-2"
                                                fullWidth
                                            >
                                                {t('property.buyMoreTokens')}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CONFIRM DIALOG --- */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {t('property.confirmUseToken')}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {t('property.tokenConfirmMessage')}
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={confirmContactReveal}
                                className="px-4 py-2 text-sm font-medium text-white bg-brand-dark hover:bg-secondary-700 rounded-lg"
                            >
                                {t('property.useToken')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

};

export default PropertyDetailsPage;
